import { useRef } from "react";
import { T, useTranslate } from "@tolgee/react";
import { CandlestickChart, Mail, Wallet } from "lucide-react";
import { motion, useInView } from "motion/react";

import { fadeUp } from "#/components/ui/motion-primitives";
import { SectionBlock } from "./section-block";

const stepKeys = [
	{
		number: "1",
		icon: Mail,
		titleKey: "started.step1-title",
		descriptionKey: "started.step1-desc",
		badgeKey: "started.step1-badge",
	},
	{
		number: "2",
		icon: Wallet,
		titleKey: "started.step2-title",
		descriptionKey: "started.step2-desc",
		badgeKey: "started.step2-badge",
	},
	{
		number: "3",
		icon: CandlestickChart,
		titleKey: "started.step3-title",
		descriptionKey: "started.step3-desc",
		badgeKey: "started.step3-badge",
	},
];

/* ── Timing: card → line → card → line → card ── */
const CARD_DUR = 0.5;
const LINE_DUR = 0.5;
const STEP = 0.35; // overlap between phases

const cardDelay = (i: number) => i * (CARD_DUR + LINE_DUR - STEP);
const lineDelay = (i: number) => cardDelay(i) + CARD_DUR - STEP;
// card0: 0,  line0: 0.15,  card1: 0.65,  line1: 0.80,  card2: 1.30

const ease = [0.16, 1, 0.3, 1] as const;

function StepCard({
	step,
	stepLabel,
}: {
	step: {
		number: string;
		icon: (typeof stepKeys)[number]["icon"];
		title: string;
		description: string;
		badge: string;
	};
	stepLabel: string;
}) {
	const Icon = step.icon;
	return (
		<div className="rounded-[24px] border border-[var(--color-theme-static-white)]/8 bg-[var(--color-theme-static-white)]/4 p-6 text-center">
			<div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-primary/20 bg-primary/12">
				<Icon className="h-5.5 w-5.5 text-primary" />
			</div>
			<div className="mx-auto mt-2 text-label2-bold text-muted-foreground">
				{stepLabel} {step.number}
			</div>
			<h3 className="mt-3 text-title2 text-foreground">
				{step.title}
			</h3>
			<p className="mt-2 text-body1 text-muted-foreground">
				{step.description}
			</p>
			<div className="mt-4 inline-flex rounded-full border border-primary/12 bg-primary/8 px-3 py-1.5 text-label2-medium text-primary">
				{step.badge}
			</div>
		</div>
	);
}

export function GettingStartedSection() {
	const { t } = useTranslate();
	const containerRef = useRef<HTMLDivElement>(null);
	const inView = useInView(containerRef, { once: true, margin: "-60px" });
	const stepLabel = t("started.step-label");
	const steps = stepKeys.map((step) => ({
		...step,
		title: t(step.titleKey),
		description: t(step.descriptionKey),
		badge: t(step.badgeKey),
	}));

	return (
		<SectionBlock
			id="getting-started"
			label={t("started.section-label")}
			title={
				<T
					keyName="started.section-title"
					params={{
						light: <span />,
						accent: <span className="text-primary" />,
						break: <br />,
					}}
				/>
			}
			description={t("started.section-description")}
			centered
		>
			<div
				ref={containerRef}
				className="mx-auto mb-18 max-w-275 px-[18px] md:px-[42px]"
			>
				{/* Mobile: stacked */}
				<div className="grid gap-6 md:hidden">
					{steps.map((step, i) => (
						<motion.div
							key={step.number}
							variants={fadeUp}
							initial="hidden"
							animate={inView ? "visible" : "hidden"}
							transition={{ duration: CARD_DUR, delay: i * 0.2, ease }}
						>
							<StepCard step={step} stepLabel={stepLabel} />
						</motion.div>
					))}
				</div>

				{/* Desktop: card → line → card → line → card */}
				<div className="hidden md:grid md:grid-cols-[1fr_48px_1fr_48px_1fr] md:items-center">
					{/* Card 1 */}
					<motion.div
						variants={fadeUp}
						initial="hidden"
						animate={inView ? "visible" : "hidden"}
						transition={{ duration: CARD_DUR, delay: cardDelay(0), ease }}
					>
						<StepCard step={steps[0]} stepLabel={stepLabel} />
					</motion.div>

					{/* Line 1 */}
					<div className="flex items-center px-1">
						<div className="h-0.5 w-full overflow-hidden rounded-full bg-[var(--color-theme-static-white)]/6">
							<motion.div
								className="h-full rounded-full bg-primary/50"
								initial={{ scaleX: 0 }}
								animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
								transition={{ duration: LINE_DUR, delay: lineDelay(0), ease }}
								style={{ transformOrigin: "left" }}
							/>
						</div>
					</div>

					{/* Card 2 */}
					<motion.div
						variants={fadeUp}
						initial="hidden"
						animate={inView ? "visible" : "hidden"}
						transition={{ duration: CARD_DUR, delay: cardDelay(1), ease }}
					>
						<StepCard step={steps[1]} stepLabel={stepLabel} />
					</motion.div>

					{/* Line 2 */}
					<div className="flex items-center px-1">
						<div className="h-0.5 w-full overflow-hidden rounded-full bg-[var(--color-theme-static-white)]/6">
							<motion.div
								className="h-full rounded-full bg-primary/50"
								initial={{ scaleX: 0 }}
								animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
								transition={{ duration: LINE_DUR, delay: lineDelay(1), ease }}
								style={{ transformOrigin: "left" }}
							/>
						</div>
					</div>

					{/* Card 3 */}
					<motion.div
						variants={fadeUp}
						initial="hidden"
						animate={inView ? "visible" : "hidden"}
						transition={{ duration: CARD_DUR, delay: cardDelay(2), ease }}
					>
						<StepCard step={steps[2]} stepLabel={stepLabel} />
					</motion.div>
				</div>
			</div>
		</SectionBlock>
	);
}
